import { twMerge } from 'tailwind-merge';
import { AnimatePresence, motion } from 'motion/react';
import { memo, PropsWithChildren, ReactNode } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import ConditionalRenderer from '../ConditionalRenderer';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { accordion } from '@/utils/animations/commom';
import TextWithTooltip from '../dataDisplay/TextWithTooltip';

type ModularCardProps = PropsWithChildren & {
  render?: boolean;
  setRef?: (ref: HTMLElement) => void;
  className?: Pick<ClassNameComponent, 'base'>;
};

export const ModularCardBase = memo(function ModularCard(
  props: ModularCardProps,
) {
  const { render = true, setRef, className, children } = props;

  return (
    <ConditionalRenderer condition={render}>
      <article
        ref={setRef}
        className={twMerge(
          'rounded-xl shadow-default bg-base-100 border border-neutral-content flex flex-col',
          className?.base,
        )}
      >
        {children}
      </article>
    </ConditionalRenderer>
  );
});

const CardIconAnimation = motion.create(ChevronDownIcon);

type HeaderProps = PropsWithChildren & {
  title?: string;
  icon?: ReactNode;
  className?: Pick<
    ClassNameComponent,
    'base' | 'section' | 'title' | 'figure' | 'button' | 'text'
  >;
  onOpen?(): void;
  isOpen?: boolean;
  hideTooltip?: boolean;
  titleFallback?: JSX.Element;
  onClick?: () => void;
};

const Header = memo(function Header(props: HeaderProps) {
  const {
    className,
    children,
    onOpen,
    isOpen,
    title,
    icon,
    hideTooltip,
    titleFallback,
    onClick,
  } = props;

  return (
    <header
      data-testid="headerButton"
      role="button"
      onClick={e => {
        e.stopPropagation();
        onOpen?.();
        onClick?.();
      }}
      className={twMerge(
        'flex gap-2 relative items-center p-3.5 text-nowrap',
        className?.base,
      )}
    >
      <section
        className={twMerge(
          'flex gap-2 items-center h-fit max-w-[75%] select-none',
          className?.section,
        )}
      >
        <ConditionalRenderer condition={icon}>
          <figure
            className={twMerge(
              'flex w-5 h-5 bg-primary-content rounded-full items-center justify-center',
              className?.figure,
            )}
          >
            {icon}
          </figure>
        </ConditionalRenderer>

        <ConditionalRenderer condition={!titleFallback}>
          <TextWithTooltip
            hideTooltip={hideTooltip}
            text={title}
            className={twMerge(
              'text-[0.875rem] lg:text-[1rem] text-base-content font-600 text-poppins',
              className?.title,
            )}
          />
        </ConditionalRenderer>
        {titleFallback}
      </section>
      <span onClick={e => e.stopPropagation()}>{children}</span>

      <ConditionalRenderer condition={onOpen}>
        <button
          type="button"
          className={twMerge(
            'absolute right-4 text-base-content justify-self-end',
            className?.button,
          )}
        >
          <CardIconAnimation
            animate={{
              rotate: isOpen ? 180 : 0,
            }}
            className="w-5"
          />
        </button>
      </ConditionalRenderer>
    </header>
  );
});

type DivisionProps = {
  className?: Pick<ClassNameComponent, 'base'>;
};

const Division = memo(function Division(props: DivisionProps) {
  const { className } = props;

  return <hr className={twMerge('h-px border-primary/40', className?.base)} />;
});

type AccordionProps = PropsWithChildren & {
  className?: Pick<ClassNameComponent, 'base'>;
  isOpen?: boolean;
};

const Accordion = memo(function Accordion(props: AccordionProps) {
  const { className, children, isOpen } = props;
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.section
          data-testid="accordion"
          {...accordion}
          className={twMerge('flex flex-col overflow-hidden', className?.base)}
        >
          {children}
        </motion.section>
      )}
    </AnimatePresence>
  );
});

type ModularCardType = typeof ModularCardBase & {
  Header: typeof Header;
  Division: typeof Division;
  Accordion: typeof Accordion;
};

export const ModularCard = ModularCardBase as ModularCardType;

ModularCard.Header = Header;
ModularCard.Division = Division;
ModularCard.Accordion = Accordion;
